import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    BarChart3, Send, Loader2, Copy, Download, Printer, Check, Sparkles,
    ChartLine, ChartArea, ChartPie, Table as TableIcon,
} from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
    PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { sendAssistantMessage } from '../redux/assistantSlice';
import { isSystemId } from '../utils/systemId';
import { assistantErrorText } from '../utils/assistantError';

// AI-Report & Analytics — ChatGPT-like page for doctor / admin / super-admin.
// Ask in plain language; the agent runs the report tools; this page renders the
// short insight text PLUS the structured results as tables + charts with
// CSV download / copy / print (save as PDF).

const SUGGESTIONS = [
    'Revenue this month, grouped by week',
    'Compare revenue this month vs last month',
    'Payments received vs outstanding this month',
    'Cancellation and no-show rates this quarter',
    'Who are my most active patients this month?',
    'List my patients with their last appointment',
];

// One palette for every series, so a status keeps its colour whichever chart
// type the reader flips to.
const SERIES_COLORS = ['#4ABDBD', '#2D8A8A', '#D7A05C', '#9DB3B1', '#6E8482', '#C7D6D5'];

const CHART_TYPES = [
    { id: 'bar', label: 'Bar', Icon: BarChart3 },
    { id: 'line', label: 'Line', Icon: ChartLine },
    { id: 'area', label: 'Area', Icon: ChartArea },
    { id: 'pie', label: 'Pie', Icon: ChartPie },
    { id: 'table', label: 'Table', Icon: TableIcon },
];

const prettify = (k) => String(k).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

function toCSV(columns, rows) {
    const esc = (v) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    return [columns.join(','), ...rows.map((r) => columns.map((c) => esc(r[c])).join(','))].join('\n');
}

function ReportCard({ report }) {
    const { t } = useTranslation(['assistant']);
    const [copied, setCopied] = useState(false);
    const columns = report.columns || [];
    // No system id on screen (owner, 29 Sep 2026). A report's `patient` /
    // `doctor` cell is a name, but the server falls back to the patientId /
    // doctorId when it has none; draw the neutral word instead. One pass here
    // covers the table, the chart labels, the CSV and the copied text.
    const rows = useMemo(() => (report.rows || []).map((r) => {
        if (!r || typeof r !== 'object') return r;
        const out = { ...r };
        // The server sends NULL for a name it does not have (30 Sep 2026); an
        // older one sent the id. Either way the cell reads "Patient" / "Doctor"
        // — never the id, and never the word "null" on a pie slice.
        const nameless = (v) => v == null || v === '' || isSystemId(v);
        if ('patient' in out && nameless(out.patient)) out.patient = t('common.patient_label', 'Patient');
        if ('doctor' in out && nameless(out.doctor)) out.doctor = t('common.doctor_label', 'Doctor');
        return out;
    }), [report.rows, t]);
    const chart = report.chart;
    const summary = report.summary || {};

    // Which measures to plot. The backend sends `series` for reports that have
    // several (appointment stats: total / completed / cancelled / no-show);
    // `y` remains the single-measure fallback.
    const series = (chart?.series?.length ? chart.series : (chart?.y ? [chart.y] : []))
        .filter((k) => columns.includes(k));

    // The reader picks the shape. The agent's suggestion is only the starting
    // point — flipping bar/line/area/pie here costs nothing and beats asking
    // the model to regenerate the same numbers in a different form.
    const [chartType, setChartType] = useState(chart?.type || 'bar');

    // A period with no activity still comes back as rows — one per bucket, all
    // zero. Charting that draws an empty grid under a "Total Revenue: 0" chip,
    // which reads as a broken chart rather than as "nothing happened yet".
    // Rows stay (the buckets are real), the chart does not.
    const hasValues = chart
        && rows.some((r) => series.some((k) => {
            const v = r?.[k];
            return typeof v === 'number' ? v !== 0 : Boolean(v);
        }));

    // Pie needs one slice per row, so it collapses to the first measure.
    const pieData = hasValues && chart
        ? rows.map((r) => ({ name: String(r[chart.x] ?? '—'), value: Number(r[series[0]]) || 0 }))
            .filter((d) => d.value > 0)
        : [];

    const downloadCSV = () => {
        const blob = new Blob([toCSV(columns, rows)], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(report.title || 'report').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const copyReport = async () => {
        const text = `${report.title}\n\n${columns.join('\t')}\n${rows.map((r) => columns.map((c) => r[c]).join('\t')).join('\n')}`;
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked */ }
    };

    return (
        <div className="mt-3 rounded-2xl border border-[#4ABDBD]/25 bg-white p-4 shadow-sm" data-testid="report-card">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <h4 className="text-sm font-bold text-[#2D8A8A]">{report.title}</h4>
                <div className="flex items-center gap-1">
                    <button type="button" onClick={copyReport} title="Copy" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100">
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <button type="button" onClick={downloadCSV} title="Download CSV" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100">
                        <Download className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* A capped result must SAY it is capped. "Top 50 of 300" read as
                "300" is the kind of silent truncation that turns a report into a
                wrong answer. */}
            {report.note && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mb-2" data-testid="report-note">
                    {report.note}
                </p>
            )}

            {/* Summary chips */}
            {Object.keys(summary).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {Object.entries(summary).map(([k, v]) => (
                        <span key={k} className="px-2.5 py-1 rounded-full bg-[#4ABDBD]/10 text-[#2D8A8A] text-xs font-semibold">
                            {k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}: {typeof v === 'number' ? v.toLocaleString() : v}
                        </span>
                    ))}
                </div>
            )}

            {/* Chart — only when there is something to plot */}
            {chart && rows.length > 0 && !hasValues && (
                <div
                    data-testid="report-no-activity"
                    className="mb-3 rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-6 text-center"
                >
                    <p className="text-sm font-semibold text-gray-500">
                        {t('aireports.no_activity', 'No activity in this period')}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                        {t('aireports.no_activity_hint', 'Every bucket is zero, so there is nothing to chart. The breakdown is below.')}
                    </p>
                </div>
            )}
            {chart && rows.length > 0 && hasValues && (
                <>
                    {/* Shape switcher */}
                    <div className="flex flex-wrap items-center gap-1 mb-2" data-testid="chart-type-switch">
                        {CHART_TYPES.map((ct) => (
                            <button
                                key={ct.id}
                                type="button"
                                data-testid={`chart-type-${ct.id}`}
                                onClick={() => setChartType(ct.id)}
                                title={ct.label}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors ${
                                    chartType === ct.id
                                        ? 'bg-[#4ABDBD] text-white border-[#4ABDBD]'
                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                {/* member expression, not a destructured local: the
                                    lint rule does not track JSX usage through
                                    destructuring and reports a false "unused". */}
                                <ct.Icon className="w-3.5 h-3.5" />
                                {ct.label}
                            </button>
                        ))}
                    </div>

                    {chartType !== 'table' && (
                        <div className="h-64 sm:h-72 mb-3">
                            <ResponsiveContainer width="100%" height="100%">
                                {chartType === 'pie' ? (
                                    <PieChart>
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Pie data={pieData} dataKey="value" nameKey="name" outerRadius="75%" label>
                                            {pieData.map((d, i) => (
                                                <Cell key={d.name} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                ) : chartType === 'line' ? (
                                    <LineChart data={rows} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey={chart.x} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                                        <YAxis tick={{ fontSize: 11 }} width={48} allowDecimals={false} />
                                        <Tooltip />
                                        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                                        {series.map((k, i) => (
                                            <Line key={k} type="monotone" dataKey={k} name={prettify(k)}
                                                stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={2} dot={false} />
                                        ))}
                                    </LineChart>
                                ) : chartType === 'area' ? (
                                    <AreaChart data={rows} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey={chart.x} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                                        <YAxis tick={{ fontSize: 11 }} width={48} allowDecimals={false} />
                                        <Tooltip />
                                        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                                        {series.map((k, i) => (
                                            <Area key={k} type="monotone" dataKey={k} name={prettify(k)}
                                                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                                                fill={SERIES_COLORS[i % SERIES_COLORS.length]} fillOpacity={0.25} />
                                        ))}
                                    </AreaChart>
                                ) : (
                                    <BarChart data={rows} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey={chart.x} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                                        <YAxis tick={{ fontSize: 11 }} width={48} allowDecimals={false} />
                                        <Tooltip />
                                        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                                        {series.map((k, i) => (
                                            <Bar key={k} dataKey={k} name={prettify(k)}
                                                fill={SERIES_COLORS[i % SERIES_COLORS.length]} radius={[4, 4, 0, 0]} />
                                        ))}
                                    </BarChart>
                                )}
                            </ResponsiveContainer>
                        </div>
                    )}
                </>
            )}

            {/* Table */}
            {rows.length > 0 ? (
                <div className="overflow-x-auto custom-scrollbar rounded-xl border border-gray-100">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {columns.map((c) => (
                                    <th key={c} className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">{c.replace(/_/g, ' ')}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i} className="border-t border-gray-100">
                                    {columns.map((c) => (
                                        <td key={c} className="px-3 py-1.5 text-gray-700 whitespace-nowrap">
                                            {typeof r[c] === 'number' ? r[c].toLocaleString() : r[c]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-sm text-gray-400">No data for this period.</p>
            )}
        </div>
    );
}

export default function AIReports() {
    const { t, i18n } = useTranslation(['assistant']);
    const dispatch = useDispatch();
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [turns, setTurns] = useState([]); // { from:'user'|'bot', text, reports? }
    const sessionIdRef = useRef(`r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const bottomRef = useRef(null);

    useEffect(() => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [turns]);

    const ask = async (textOverride) => {
        const text = (textOverride ?? input).trim();
        if (!text || busy) return;
        setInput('');
        setTurns((m) => [...m, { from: 'user', text }]);
        setBusy(true);
        try {
            const res = await dispatch(sendAssistantMessage({ message: text, sessionId: sessionIdRef.current, surface: 'reports' })).unwrap();
            const reports = Array.isArray(res?.reports) ? res.reports : [];
            setTurns((m) => [...m, {
                from: 'bot',
                // A report with no words is still an answer (the table and
                // chart below); no report and no words is not — say so
                // instead of the bare "…" that read as "the AI is broken".
                text: res?.reply || (reports.length ? '' : t('assistant.empty_reply', 'Sorry, I could not put an answer together. Could you rephrase the question?')),
                reports,
                options: Array.isArray(res?.options) ? res.options : [],
            }]);
        } catch (err) {
            // Why it failed, in words: a hospital switch or a plan that lacks
            // the feature is not worth a retry, a timeout or a rate limit is.
            setTurns((m) => [...m, { from: 'bot', text: assistantErrorText(err, t, i18n.language), reports: [], options: [], failed: true }]);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="w-full flex flex-col h-full" data-testid="ai-reports-page">
            {/* Header */}
            <div className="flex items-center justify-between py-4 px-1">
                <div className="flex items-center gap-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] text-white">
                        <BarChart3 className="w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-[#2D8A8A]">{t('assistant.reports_title', 'AI Reports & Analytics')}</h1>
                        <p className="text-xs text-gray-400">{t('assistant.reports_sub', 'Ask for revenue, payments, appointment stats or patient activity in plain language.')}</p>
                    </div>
                </div>
                <button type="button" onClick={() => window.print()} title={t('assistant.print', 'Print / save as PDF')} className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">
                    <Printer className="w-5 h-5" />
                </button>
            </div>

            {/* Conversation */}
            <div className="flex-1 overflow-y-auto custom-scrollbar px-1 pb-4" data-testid="ai-reports-conversation">
                {turns.length === 0 && (
                    <div className="text-center mt-14">
                        <Sparkles className="w-8 h-8 mx-auto mb-3 text-[#4ABDBD]/60" />
                        <p className="text-sm text-gray-400 mb-5">{t('assistant.reports_hint', 'Try one of these:')}</p>
                        <div className="flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
                            {SUGGESTIONS.map((s) => (
                                <button key={s} type="button" onClick={() => ask(s)} className="px-3 py-1.5 rounded-full border border-[#4ABDBD]/30 text-[#2D8A8A] text-xs hover:bg-[#4ABDBD]/10">
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {turns.map((m, i) => (
                    <div key={i} className={`mt-4 flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`${m.from === 'user' ? 'max-w-[75%] bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-2xl rounded-br-sm px-4 py-2 text-sm' : 'w-full'}`}>
                            {m.from === 'user' ? m.text : (
                                <div>
                                    {m.text && (
                                        <p
                                            className={`text-sm whitespace-pre-line ${m.failed ? 'text-amber-700' : 'text-gray-700'}`}
                                            data-testid={m.failed ? 'ai-reports-error' : undefined}
                                        >
                                            {m.text}
                                        </p>
                                    )}
                                    {/* Tappable answers. The agent asks how you want the
                                        numbers presented when the request does not say —
                                        clicking is faster than typing "as a pie chart". */}
                                    {i === turns.length - 1 && !busy && m.options?.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="report-options">
                                            {m.options.map((opt) => (
                                                <button
                                                    key={opt}
                                                    type="button"
                                                    onClick={() => ask(opt)}
                                                    className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#4ABDBD] text-[#2D8A8A] bg-white hover:bg-[#4ABDBD]/10 transition-colors"
                                                >
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {(m.reports || []).map((r, j) => <ReportCard key={j} report={r} />)}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {busy && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
                        <Loader2 className="w-4 h-4 animate-spin text-[#3AA8A8]" /> {t('assistant.working', 'Crunching the numbers…')}
                    </div>
                )}
                <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="border-t border-gray-100 bg-white rounded-2xl shadow-sm p-3 mb-2 flex items-end gap-2">
                <textarea
                    data-testid="ai-reports-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
                    placeholder={t('assistant.reports_placeholder', 'e.g. Compare my revenue this month vs last month…')}
                    rows={2}
                    className="flex-1 resize-none px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40"
                />
                <button
                    type="button"
                    data-testid="ai-reports-send"
                    onClick={() => ask()}
                    disabled={busy || !input.trim()}
                    className="p-3 rounded-xl text-white bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] disabled:opacity-40"
                >
                    <Send className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
